import { useEffect, useState } from "react";
import { Contact, ClipboardPaste, X } from "lucide-react";
import { toast } from "sonner";
import { validEmail, formatPhone, defaultRegion } from "../lib/phone";
import { PhoneInput } from "./PhoneInput";
import { useProfile } from "../hooks/useProfile";
import { CrewChips } from "./CrewChips";
import { useCrew, findCrew } from "../lib/crew";

const supportsPicker = () => typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window && typeof navigator.contacts?.select === "function";
const debugOn = () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debugContacts") === "1";
const BLOCKED_MSG = "Your browser blocked the contact picker (Brave/Firefox/iPhone don't support it). Use Paste or type the number.";
const BRAVE_MSG = "Your browser (Brave) blocks the contact picker. Tap Paste number, or open DropIt in Chrome.";
let isBrave = false;
if (typeof navigator !== "undefined") { try { Promise.resolve(navigator.brave?.isBrave?.()).then((v) => { isBrave = !!v; }); } catch { /* not brave */ } }
const failMsg = (e) => isBrave || /brave/i.test(navigator.userAgent) ? BRAVE_MSG : /unable to open|not allowed|blocked|denied|SecurityError|NotAllowedError/i.test(`${e?.name} ${e?.message}`) ? `${BLOCKED_MSG} (${e?.name || "Error"}: ${e?.message || "no details"})` : `Contact picker failed: ${e?.name || "Error"} — ${e?.message || BLOCKED_MSG}`;

export const ContactFields = ({ contact, onChange }) => {
  const { profile } = useProfile() || {};
  const emailOk = !contact.email || validEmail(contact.email);
  const hasPicker = supportsPicker();
  const [chooser, setChooser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(() => (debugOn() ? [`support: contacts in navigator=${"contacts" in navigator}, ContactsManager=${"ContactsManager" in window}, select=${typeof navigator.contacts?.select}, ua=${navigator.userAgent}`] : null));
  const dbg = (m) => { if (log) setLog((l) => [...l, `${new Date().toLocaleTimeString()} ${m}`]); };
  const region = contact.region || defaultRegion(profile?.phone);
  const { rows: crew } = useCrew();
  const known = crew ? findCrew(crew, { phone: contact.phone, email: contact.email }) : null;
  const isNew = !!(contact.phone || (contact.email && emailOk)) && !known && !contact.crewContactId;
  useEffect(() => { if (known && contact.crewContactId !== known.id) onChange({ ...contact, crewContactId: known.id, contactName: contact.contactName || known.name, saveToCrew: false }); }, [known?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveOn = contact.saveToCrew !== false;
  const pickCrew = (c) => onChange({ ...contact, crewContactId: c.id, contactName: c.name, phoneRaw: c.phone || "", phone: c.phone || "", email: c.email || "", saveToCrew: false });
  const clearCrew = () => onChange({ ...contact, crewContactId: null, contactName: "", phoneRaw: "", phone: "", email: "" });
  const edited = (patch) => {
    const next = { ...contact, ...patch };
    const sel = contact.crewContactId && crew?.find((c) => c.id === contact.crewContactId);
    const keep = sel && (next.phone || "") === (sel.phone || "") && (next.email || "") === (sel.email || "");
    onChange(keep ? next : { ...next, crewContactId: null });
  };

  const apply = (tel, email, name) => {
    const next = { ...contact, contactName: name || contact.contactName || "", crewContactId: null };
    if (tel) { const f = formatPhone(tel, region); next.phoneRaw = f.display; next.phone = f.e164; }
    if (email) next.email = email;
    onChange(next);
    toast.success(name ? `Added ${name}` : "Contact added");
  };

  const handleResult = (res) => {
    dbg(`select resolved: ${JSON.stringify(res)}`);
    if (!Array.isArray(res) || !res.length) return toast.error(res === undefined ? (isBrave ? BRAVE_MSG : BLOCKED_MSG) : "No contact selected");
    const c = res[0];
    const tels = (c.tel || []).map((t) => String(t).trim()).filter(Boolean);
    const emails = (c.email || []).map((e) => String(e).trim()).filter(Boolean);
    const name = (c.name || [])[0] || "";
    if (!tels.length && !emails.length) return toast.error("That contact has no phone number or email");
    if (tels.length > 1 || emails.length > 1) return setChooser({ name, tels, emails, tel: tels[0] || "", email: emails[0] || "" });
    apply(tels[0], emails[0], name);
  };

  const pick = () => {
    if (!hasPicker) return toast.error(BLOCKED_MSG);
    setBusy(true);
    let p;
    try {
      p = navigator.contacts.select(["tel", "name", "email"], { multiple: false });
      dbg(`select() called, returned ${p && typeof p.then === "function" ? "promise" : String(p)}`);
    } catch (e) {
      setBusy(false); dbg(`select threw ${e?.name}: ${e?.message}`);
      return toast.error(failMsg(e));
    }
    if (!p || typeof p.then !== "function") { setBusy(false); return toast.error(BLOCKED_MSG); }
    const timeout = new Promise((_, rej) => setTimeout(() => rej(Object.assign(new Error("The contact picker did not respond — your browser may block it."), { name: "TimeoutError" })), 60000));
    Promise.race([p, timeout])
      .then(handleResult)
      .catch((e) => { dbg(`select rejected ${e?.name}: ${e?.message}`); toast.error(failMsg(e)); })
      .finally(() => setBusy(false));
  };

  const paste = async () => {
    try {
      if (!navigator.clipboard?.readText) throw new Error("Clipboard access isn't available in this browser — long-press the phone field and paste instead.");
      const text = (await navigator.clipboard.readText()).trim();
      dbg(`clipboard: ${text}`);
      if (!text) return toast.error("Clipboard is empty");
      if (validEmail(text)) return apply(null, text);
      const f = formatPhone(text, region);
      if (!f.e164) return toast.error(`"${text}" doesn't look like a phone number for ${region}`);
      apply(text);
    } catch (e) { toast.error(e?.name === "NotAllowedError" ? "Clipboard permission denied — allow it in your browser settings or paste into the field." : e.message); }
  };

  return (
    <div className="space-y-3" data-testid="contact-fields">
      <CrewChips crew={crew} selectedId={contact.crewContactId} onPick={pickCrew} onClear={clearCrew} />
      <div>
        <label className="eyebrow">Contractor phone{contact.contactName && <span className="text-primary normal-case ml-2" data-testid="contact-name">· {contact.contactName}</span>}</label>
        <div className="mt-1"><PhoneInput value={contact.phoneRaw || ""} onChange={(p) => edited({ phoneRaw: p.raw, phone: p.e164, region: p.region })} testid="contact-phone" autoComplete="tel" /></div>
      </div>
      <div>
        <label className="eyebrow">Contractor email <span className="text-muted normal-case">(optional)</span></label>
        <input className="field mt-1" type="email" autoComplete="email" inputMode="email" value={contact.email || ""} placeholder="name@example.com" onChange={(e) => edited({ email: e.target.value.trim() })} data-testid="contact-email-input" />
        {!emailOk && <p className="text-xs text-[#FF7A7A] mt-1" data-testid="contact-email-error">Enter a valid email.</p>}
      </div>
      {isNew && (
        <div className="card p-3 bg-[var(--surface-2)]" data-testid="save-to-crew">
          <div className="flex items-center gap-3">
            <button type="button" role="switch" aria-checked={saveOn} className="toggle" onClick={() => onChange({ ...contact, saveToCrew: !saveOn })} data-testid="save-to-crew-toggle" />
            <div className="flex-1"><p className="text-sm font-bold">Save to crew</p><p className="text-xs text-muted">One-tap next time.</p></div>
          </div>
          {saveOn && <input className="field mt-3" placeholder="Name (required)" value={contact.contactName || ""} onChange={(e) => onChange({ ...contact, contactName: e.target.value })} required data-testid="crew-save-name-input" />}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" className="btn-secondary flex-1 flex items-center justify-center gap-2" onClick={pick} disabled={!hasPicker || busy} data-testid="contact-picker-button" data-supported={hasPicker}><Contact size={16} /> {busy ? "Opening…" : "Pick from contacts"}</button>
        <button type="button" className="btn-secondary w-auto px-4 flex items-center justify-center gap-2" onClick={paste} data-testid="contact-paste-button"><ClipboardPaste size={16} /> Paste number</button>
      </div>
      {!hasPicker && <p className="text-xs text-muted" data-testid="contact-picker-hint">Contact picker is Android Chrome only — on iPhone, tap the phone field and use the keyboard's contact suggestion.</p>}
      {chooser && (
        <div className="sheet-backdrop" onClick={() => setChooser(null)} data-testid="contact-chooser-backdrop">
          <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="contact-chooser">
            <div className="flex items-center justify-between"><p className="eyebrow">{chooser.name || "Contact"}</p><button type="button" className="icon-btn" onClick={() => setChooser(null)} aria-label="Close" data-testid="contact-chooser-close"><X size={16} /></button></div>
            {chooser.tels.length > 1 && <><p className="text-sm font-bold mt-3">Which number?</p>{chooser.tels.map((t) => <button type="button" key={t} className={`country-item ${chooser.tel === t ? "text-primary" : ""}`} onClick={() => setChooser({ ...chooser, tel: t })} data-testid={`chooser-tel-${t.replace(/\D/g, "")}`}>{t}</button>)}</>}
            {chooser.emails.length > 1 && <><p className="text-sm font-bold mt-3">Which email?</p>{chooser.emails.map((e) => <button type="button" key={e} className={`country-item ${chooser.email === e ? "text-primary" : ""}`} onClick={() => setChooser({ ...chooser, email: e })} data-testid={`chooser-email-${e}`}>{e}</button>)}</>}
            <button type="button" className="btn-primary mt-4" onClick={() => { apply(chooser.tel, chooser.email, chooser.name); setChooser(null); }} data-testid="contact-chooser-use">Use this</button>
          </div>
        </div>
      )}
      {log && (
        <pre className="debug-panel" data-testid="contacts-debug-panel">{log.join("\n")}</pre>
      )}
    </div>
  );
};
